import { Card, CardContent } from '@/components/ui/card';
import { Star, MapPin, Store } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from '@/lib/utils';

interface Seller {
  id: string;
  name: string;
  image_url: string;
  rating: number;
  address: string;
}

interface StockItem {
  id: string;
  name: string;
  description: string;
  price: number;
  restaurant_id: string;
  restaurant?: {
    name: string;
    address: string;
    is_open: boolean;
  };
}

interface MarketplaceSearchResultsProps {
  sellers: Seller[];
  stockItems: StockItem[];
  loading: boolean;
  onClose: () => void;
}

const MarketplaceSearchResults = ({ sellers, stockItems, loading, onClose }: MarketplaceSearchResultsProps) => {
  const navigate = useNavigate();

  const handleSellerClick = (sellerId: string) => {
    navigate(`/restaurant/${sellerId}`);
    onClose();
  };

  const handleStockItemClick = (restaurantId: string) => {
    navigate(`/restaurant/${restaurantId}`);
    onClose();
  };

  if (loading) {
    return (
      <div className="absolute top-full left-0 right-0 bg-card border border-border rounded-lg shadow-lg z-50 p-4">
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3">
              <div className="w-12 h-12 bg-muted rounded"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (sellers.length === 0 && stockItems.length === 0) return null;

  return (
    <div className="absolute top-full left-0 right-0 bg-card border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
      {sellers.length > 0 && (
        <div className="p-3">
          <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1">
            <Store className="h-4 w-4" />
            Sellers
          </h3>
          {sellers.map((seller) => (
            <Card 
              key={seller.id} 
              className="mb-2 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleSellerClick(seller.id)}
            >
              <CardContent className="p-3">
                <div className="flex gap-3 items-center">
                  {seller.image_url ? (
                    <img
                      src={seller.image_url}
                      alt={seller.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                      <Store className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium truncate">{seller.name}</h4>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        <span className="text-xs">{seller.rating.toFixed(1)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        <span className="text-xs truncate">{seller.address}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {stockItems.length > 0 && (
        <div className="p-3 border-t border-border/60">
          <h3 className="text-sm font-semibold text-foreground mb-2">Products</h3>
          {stockItems.map((item) => (
            <Card 
              key={item.id} 
              className="mb-2 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleStockItemClick(item.restaurant_id)}
            >
              <CardContent className="p-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium truncate">{item.name}</h4>
                    <p className="text-sm text-muted-foreground truncate">{item.description}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-sm font-medium">D{formatPrice(item.price)}</span>
                      {item.restaurant && (
                        <span className="text-xs text-muted-foreground truncate">
                          at {item.restaurant.name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MarketplaceSearchResults;
